import { TextFormatting, formatShortcut } from './TextFormatting';
import { typography, type ProseItem } from './typography';
import { Dialog } from '../ui/overlays';
import { useMedia } from '../ui/responsive';
import { useEffect, useRef } from 'react';
import type { Item } from '@scribbleplot/shared';
import { color, font, radius, shadow } from '../tokens';

/**
 * In-place text editing.
 *
 * Rather than a modal, the editor is positioned exactly over the item it edits
 * and inherits its typography, so committing a change causes no visible shift.
 * The item's own text is hidden while this is mounted.
 */

export type InlineEditorProps = {
  item: Item;
  accent: string;
  scale: number;
  onChange: (patch: Partial<Item>) => void;
  onClose: () => void;
};

/** Focus and place the caret at the end when the editor appears. */
function useAutoFocus<T extends HTMLInputElement | HTMLTextAreaElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    try {
      const end = el.value.length;
      el.setSelectionRange(end, end);
    } catch {
      // Some input types disallow selection ranges; focusing is enough.
    }
  }, []);
  return ref;
}

export function InlineEditor({ item, accent, scale, onChange, onClose }: InlineEditorProps) {
  const phone = useMedia('(max-width: 767px)');
  if (phone) return <MobileEditor item={item} onChange={onChange} onClose={onClose} />;
  const border = `${(1.6 / scale).toFixed(2)}px solid ${accent}`;

  /** Esc always closes; Enter closes for single-line fields, Cmd+Enter for prose. */
  const onKeyDown = (event: React.KeyboardEvent, singleLine: boolean) => {
    event.stopPropagation();
    const commitCombo = (event.metaKey || event.ctrlKey) && event.key === 'Enter';
    if (event.key === 'Escape' || commitCombo || (singleLine && event.key === 'Enter')) {
      event.preventDefault();
      onClose();
    }
  };

  if (item.type === 'sticky' || item.type === 'text') {
    return <ProseEditor item={item} border={border} onChange={onChange} onClose={onClose} />;
  }

  if (item.type === 'frame') {
    return (
      <FrameInput
        value={item.label}
        onChange={(label) => onChange({ label } as Partial<Item>)}
        onKeyDown={(event) => onKeyDown(event, true)}
        style={{
          position: 'absolute',
          left: item.x,
          top: item.y - 34,
          width: 240,
          height: 26,
          zIndex: 99_999,
          background: color.panel,
          border: `1.5px solid ${accent}`,
          borderRadius: 7,
          color: color.text,
          fontFamily: font.mono,
          fontSize: 11,
          letterSpacing: '.09em',
          padding: '0 9px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  if (item.type === 'link') {
    return (
      <div
        onPointerDown={(event) => event.stopPropagation()}
        style={{
          position: 'absolute',
          left: item.x,
          top: item.y + item.h + 10,
          width: Math.max(240, item.w),
          zIndex: 99_999,
          background: color.panel,
          border: `1px solid rgba(255,255,255,.15)`,
          borderRadius: radius.lg,
          padding: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          boxShadow: shadow.panel,
        }}
      >
        <LinkField
          value={item.title}
          placeholder="Title"
          autoFocus
          onChange={(title) => onChange({ title } as Partial<Item>)}
          onKeyDown={(event) => onKeyDown(event, true)}
        />
        <LinkField
          value={item.url}
          placeholder="URL"
          onChange={(url) => onChange({ url } as Partial<Item>)}
          onKeyDown={(event) => onKeyDown(event, true)}
        />
        <button
          type="button"
          onClick={onClose}
          style={{
            alignSelf: 'flex-end',
            height: 26,
            padding: '0 12px',
            borderRadius: 7,
            border: 'none',
            background: accent,
            color: color.onAccent,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    );
  }

  return null;
}

function ProseEditor({
  item,
  border,
  onChange,
  onClose,
}: {
  item: ProseItem;
  border: string;
  onChange: InlineEditorProps['onChange'];
  onClose: () => void;
}) {
  const ref = useAutoFocus<HTMLTextAreaElement>();
  const fontControl = useRef<HTMLSelectElement>(null);
  return (
    <>
      <textarea
        ref={ref}
        aria-label="Text"
        value={item.text}
        onChange={(e) => onChange({ text: e.target.value } as Partial<Item>)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            fontControl.current?.focus();
          }
          formatShortcut(e, item, (textStyle) => onChange({ textStyle }));
          if (e.key === 'Escape' || ((e.metaKey || e.ctrlKey) && e.key === 'Enter')) {
            e.preventDefault();
            onClose();
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          left: item.x,
          top: item.y,
          width: item.w,
          height: item.h,
          zIndex: 99_999,
          border,
          outline: 'none',
          resize: 'none',
          boxSizing: 'border-box',
          background: item.type === 'sticky' ? item.color : 'rgba(20,21,25,.6)',
          borderRadius: item.type === 'sticky' ? 3 : 8,
          padding: item.type === 'sticky' ? 13 : 6,
          ...typography(item),
        }}
      />
      <TextFormatting
        item={item}
        change={(textStyle) => onChange({ textStyle })}
        editor={ref}
        fontControl={fontControl}
        onClose={onClose}
        floating
      />
    </>
  );
}

function FrameInput({
  value,
  onChange,
  onKeyDown,
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  style: React.CSSProperties;
}) {
  const ref = useAutoFocus<HTMLInputElement>();
  return (
    <input
      ref={ref}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      onPointerDown={(event) => event.stopPropagation()}
      style={style}
    />
  );
}

function LinkField({
  value,
  onChange,
  onKeyDown,
  placeholder,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      aria-label={placeholder}
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      style={{
        background: color.input,
        border: `1px solid ${color.lineStrong}`,
        borderRadius: 8,
        color: color.text,
        fontSize: 13,
        fontFamily: 'inherit',
        padding: '7px 9px',
        outline: 'none',
      }}
    />
  );
}

function MobileEditor({ item, onChange, onClose }: Pick<InlineEditorProps, 'item' | 'onChange' | 'onClose'>) {
  const editor = useRef<HTMLTextAreaElement>(null);
  if (item.type === 'image' || item.type === 'group') return null;
  return (
    <Dialog
      title={item.type === 'frame' ? 'Rename area' : 'Edit text'}
      onClose={onClose}
      initialFocus={item.type === 'sticky' || item.type === 'text' ? 'textarea' : 'input'}
    >
      <div className="sp-mobile-editor" onKeyDown={(e) => e.stopPropagation()}>
        {(item.type === 'sticky' || item.type === 'text') && (
          <>
            <TextFormatting
              item={item}
              change={(textStyle) => onChange({ textStyle })}
              editor={editor}
              onClose={onClose}
            />
            <label>
              Text
              <textarea
                ref={editor}
                style={{ ...typography(item), background: item.type === 'sticky' ? item.color : color.input }}
                onKeyDown={(e) => {
                  formatShortcut(e, item, (textStyle) => onChange({ textStyle }));
                  if (e.key === 'Escape' || ((e.metaKey || e.ctrlKey) && e.key === 'Enter')) {
                    e.preventDefault();
                    onClose();
                  }
                }}
                aria-label="Text"
                value={item.text}
                onChange={(e) => onChange({ text: e.target.value } as Partial<Item>)}
              />
            </label>
          </>
        )}
        {item.type === 'frame' && (
          <label>
            Area name
            <input value={item.label} onChange={(e) => onChange({ label: e.target.value } as Partial<Item>)} />
          </label>
        )}
        {item.type === 'link' && (
          <>
            <label>
              Title
              <input value={item.title} onChange={(e) => onChange({ title: e.target.value } as Partial<Item>)} />
            </label>
            <label>
              URL
              <input value={item.url} onChange={(e) => onChange({ url: e.target.value } as Partial<Item>)} />
            </label>
          </>
        )}
        <button className="sp-button sp-primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}
